import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import SectionCard from "@/components/customers/SectionCard";
import { useCustomerSignals, type CustomerSignalWithRefs } from "@/hooks/use-customer-signals";
import { formatRelativeTime } from "@/lib/format";
import { signalSourceChip, signalSourceLabel, signalTypeLabel } from "@/lib/signal-types";

function statusText(signal: CustomerSignalWithRefs): string {
  switch (signal.status) {
    case "open":
      return "Åpent";
    case "claimed":
      return signal.claimer ? `Tatt av ${signal.claimer.full_name}` : "Tatt";
    case "archived":
      return "Arkivert";
    case "expired":
      return "Utløpt";
  }
}

/**
 * «Hva skjer hos dem» (CRM-3.5): de siste signalene på kunden med kildemerke,
 * hva som skjedde, kilde og alder, og status. Et åpent signal har forslag til
 * første steg; det tas i køen. Uten signaler er kortet én linje.
 */
export default function CustomerSignalFeed({ customerId }: { customerId: string }) {
  const { data: signals = [], isLoading } = useCustomerSignals(customerId);

  return (
    <SectionCard
      compact
      title="Hva skjer hos dem"
      meta={signals.length > 0 ? `${signals.length} ${signals.length === 1 ? "signal" : "signaler"}` : undefined}
      empty="Ingen signaler ennå. Kildene under Overvåkning sjekkes etter frekvensen sin."
    >
      {isLoading ? (
        <Skeleton className="m-4 h-16" />
      ) : signals.length === 0 ? null : (
        <ul className="divide-y divide-border px-4">
          {signals.map((s) => (
            <li key={s.id} className="flex gap-3 py-2.5 text-sm">
              <span
                className="mt-0.5 inline-flex h-6 w-9 shrink-0 items-center justify-center bg-muted text-[10px] font-semibold tracking-wide"
                aria-label={signalSourceLabel(s.source)}
              >
                {signalSourceChip(s.source)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  <span className="text-muted-foreground">{signalTypeLabel(s.signal_type)}:</span> {s.headline}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  <span>
                    {signalSourceLabel(s.source)} · {formatRelativeTime(s.created_at)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{statusText(s)}</span>
                  {s.status === "open" && (
                    <Link to="/salgsmuligheter" className="text-primary hover:underline">
                      har forslag
                    </Link>
                  )}
                  {s.status === "claimed" && s.opportunity_id && (
                    <Link to="/salgsmuligheter/pipe" className="text-primary hover:underline">
                      Se i Pipe
                    </Link>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
